<template lang="html">
  <div class="ui form clearing margin-top-medium" @keypress.enter="save">
    <event-calendar-selection
      :can-update-calendar="canUpdateCalendar"
      @selectedCalendarId="setCalendarId"
      @selectedUserId="setUserId"
      @selectedCategoryId="setCategoryId"
    />

    <div class="ui divider margin-y-small" />

    <div class="fields">
      <div class="six wide field">
        <label> Sponsor</label>
        <select
          ref="selectSponsor"
          v-model="event.organization_id"
          data-additions="true"
          data-clearable="true"
          data-forceselection="true"
          data-placeholder="Type to add a sponsor"
          class="ui search compact dropdown selection"
        >
          <option value="">No sponsor</option>
          <option
            v-for="option in sponsors"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </div>

      <div v-if="calendar && calendar.use_rates" class="five wide field">
        <label>
          Rate
          <span
            :data-tooltip="`Only ${calendar.name} admins and ${event.username} can see this.`"
            data-inverted="true"
            data-variation="mini"
          >
            <i class="icon question mark circle grey small" />
          </span>
        </label>

        <div class="ui left icon input">
          <input v-model.number="event.rate" type="number" inputmode="decimal" />
          <i class="icon dollar sign" />
        </div>
      </div>

      <div v-if="showStatusField" class="five wide field">
        <label>Status</label>
        <select
          v-model.lazy="event.aasm_state"
          class="ui dropdown selection padding-y-tiny"
        >
          <option
            v-for="option in eventStates"
            :key="option"
            :value="option"
            :selected="option === event.aasm_state"
          >
            <div :class="option" class="ui label small">
              {{ option | capitalize }}
            </div>
          </option>
        </select>
      </div>
    </div>

    <div class="fields datepair">
      <div class="six wide field">
        <label>
          <!-- <i class="icon calendar alternate"></i> -->
          Date
        </label>
        <div>
          <div id="start_date" class="ui calendar field">
            <div class="ui input">
              <input
                :value="calendarFormattedDateTime(event.starts_at)"
                class="date start"
              />
            </div>
          </div>
        </div>
      </div>

<!-- No `desktop-only` here: that class made end dates unreachable on
           phones (mobile web AND the native app) — the toggle and the field
           were both hidden with no other path to set an end date. -->
      <div v-show="showEndDate" class="five wide field">
        <label>End date</label>

        <div id="end_date" class="ui calendar field">
          <div class="ui input">
            <input
              :value="calendarFormattedDateTime(event.ends_at)"
              class="date end"
            />
          </div>
        </div>
      </div>

      <div class="field end-date-toggle">
        <label>&nbsp;</label>
        <div class="ui text small margin-top-small">
          <a href="#" @click.stop.prevent="toggleEndDate">
            <span v-if="showEndDate">- end date</span>
            <span v-else>+ end date (optional)</span>
          </a>
        </div>
      </div>
    </div>

    <div class="fields">
      <div class="field sixteen wide">
        <label>Tags</label>
        <div
          :key="availableTags.length"
          ref="tagList"
          class="ui multiple search selection dropdown"
          data-additions="true"
        >
          <input v-model.lazy="event.tag_list" type="hidden" />

          <i v-if="availableTags.length > 0" class="dropdown icon" />

          <div class="default text">Type and hit enter to add a tag</div>
          <div class="menu">
            <div
              v-for="option in availableTags"
              :data-value="option.name"
              class="item"
            >
              {{ option.name }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ui field">
      <label class="label"> Description </label>

      <text-editor
        :key="event.id + 'editor'"
        v-model="event.description"
        class="editor"
        :autofocus="false"
      />

      <uploads-index-view
        :key="'uploader-event-' + event.id"
        :url="uploadsUrl"
        :allow-uploads="true"
        class="margin-top-small"
        @complete="addUpload"
      />
    </div>

    <div v-if="event.id && wordpressEnabled" class="field">
      <div class="ui checkbox">
        <input
          v-model="event.wordpress_sync_enabled"
          type="checkbox"
          name="sync"
        />
        <label>
          <span v-if="event.last_synced_at"> Synced to Wordpress </span>
          <span
            v-else
            data-tooltip="Not synced yet."
            data-inverted="true"
            data-variation="mini"
          >
            Sync to Wordpress
          </span>
        </label>
      </div>
      <br />
      <span
        v-if="event.wordpress_edit_url"
        data-tooltip="Open in Wordpress"
        data-inverted="true"
        data-variation="mini"
        class="ui text small"
      >
        Last synced:
        <a :href="event.wordpress_edit_url" target="_blank">
          <span v-if="event.last_synced_at">
            {{ event.last_synced_at }}
          </span>

          <i class="icon external alternate small" />
        </a>
      </span>
    </div>

<!-- Native app: the form autosaves (debouncedUpdate), so the web's three
         terminal actions collapse to a grouped-form destructive row (iOS
         pattern) + one sticky primary "Done" (save & close). Web keeps its
         Delete / Save & close / Save footer untouched. -->
    <template v-if="isNativeApp">
      <a
        v-if="event.id"
        :href="url"
        class="native-delete-row"
        @click.stop.prevent="destroyAndClose"
      >
        Delete event
      </a>

      <div class="native-action-bar">
        <a
          class="ui button primary fluid"
          :class="{ loading }"
          @click.stop.prevent="saveAndClose"
        >
          Done
        </a>
      </div>
    </template>

    <div v-else class="clearing">
      <a
        v-if="event.id"
        :href="url"
        class="ui labeled icon button red left floated"
        @click.stop.prevent="destroyAndClose"
      >
        <i class="icon trash alternate" />
        Delete
      </a>

      <div class="ui right floated">
        <div class="ui buttons">
          <a
            v-shortkey="['shift', 'c']"
            class="ui button"
            @shortkey="saveAndClose"
            @click.stop.prevent="saveAndClose"
          >
            Save & close
          </a>
          <a
            v-shortkey="['shift', 's']"
            class="ui button primary"
            :class="{ loading }"
            @shortkey="save"
            @click="save"
          >
            Save
          </a>
        </div>
      </div>
    </div>

    <div v-if="error" class="ui error message visible">
      <div class="ui relaxed list">
        <div v-for="err in error" class="item">
          {{ err }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import "idempotent-babel-polyfill";
// import "document-register-element/build/document-register-element";
import { mapState, mapActions } from "vuex";
import store from "store";

import UploadsIndexView from "views/uploads/index.vue";
import EventCalendarSelection from "views/events/form/calendar.vue";
import textEditor from "components/textEditor.vue";

import ResizeObserver from "resize-observer-polyfill";
import "functions/filters";

const { $ } = window;
const { moment } = window;

export default {
  store,
  components: {
    textEditor,
    UploadsIndexView,
    EventCalendarSelection,
  },
  filters: {},
  props: [
    "initialEvent",
    "calendar",
    // "calendars",
    "eventStates",
    "showStatusField",
    "canUpdateCalendar",
    "reloadAfterSave",
    "wordpressEnabled",
  ],
  data() {
    return {
      closeAfterSave: false,
      availableTags: [],
      loading: false,
      // event: $this.initialEvent,
      showEndDate: false,
      showDescription: false,
      selectedCalendar: null,
      eventCategories: [],
      users: [],
    };
  },
  computed: {
    ...mapState("event", {
      event: "current",
      error: "error",
    }),
    ...mapState("sponsor", {
      sponsors: "all",
    }),
    uploadsUrl() {
      return `/uploads?assetable_type=Event&assetable_id=${this.event.id}`;
    },
    endpoint() {
      return `/calendars/${this.calendar.id}/events`;
    },
    url() {
      let url = `${this.endpoint}`;
      if (this.event.id) {
        url += `/${this.event.id}`;
      }
      return url;
    },
    requestType() {
      let type = "post";
      if (this.event.id) {
        type = "patch";
      }
      return type;
    },
    isNativeApp() {
      return document.documentElement.hasAttribute("data-hotwire-native");
    },
  },
  methods: {
    ...mapActions("event", {
      setEvent: "setCurrent",
      create: "create",
      update: "update",
      destroy: "destroy",
    }),
    reloadSuggestedTasks() {
      if (this.event.id && this.event.tasks_count == 0) {
        this.$store.dispatch("task/loadAll", { parentEvent: this.event });
      }
    },
    destroyAndClose() {
      if (confirm("Are you sure you want to delete this?")) {
        this.destroy(this.event).then(() => {
          this.closeModal();
        });
      }
    },
    save() {
      this.loading = true;

      if (this.event.id) {
        this.update(this.event).finally(() => {
          this.loading = false;
          if (this.closeAfterSave) {
            this.closeModal();
          }
        });
      } else {
        this.create(this.event).finally(() => {
          this.loading = false;
          this.reloadSuggestedTasks();
        });
      }
    },
    saveAndClose() {
      this.closeAfterSave = true;
      this.save();
    },
    calendarFormattedDateTime(dateTime) {
      if (!dateTime || dateTime === "") {
        return "";
      }
      const m = moment(dateTime);
      // Native app: an untimed event (midnight) shows just the date —
      // "July 5, 2026 12:00 AM" is time noise on a phone. Web unchanged.
      if (this.isNativeApp && m.format("h:mm A") === "12:00 AM") {
        return m.format("MMMM D, YYYY");
      }
      return m.format("MMMM D, YYYY h:mm A");
    },
    setCalendarId(val) {
      this.event.calendar_id = val;
    },
    setUserId(id, login) {
      this.event.user_id = id;
      this.event.username = login;
    },
    setCategoryId(val) {
      this.event.event_category_id = val;
    },
    toggleEndDate() {
      this.showEndDate = !this.showEndDate;
      if (!this.showEndDate) {
        this.event.ends_at = null;
      }
    },

    addUpload(upload) {
      this.event.upload_ids = this.event.upload_ids || [];
      this.event.upload_ids.push(upload.id);
    },
    clearSponsor() {
      $(this.$refs.selectSponsor).dropdown("clear");
    },
    loadTags() {
      const $this = this;
      $.get("/tags.json").done((data) => {
        $this.availableTags = data;
      });
    },
    initDropdowns() {
      $(this.$el)
        .find(".dropdown")
        .each((i, element) => {
          if (!$(element).data("dropdown-loaded")) {
            // Fomantic moves a source <select>'s classes onto its wrapper
            // div, so on a re-init pass `element` can be the wrapper while
            // the data-* settings still live on the select inside it.
            const $settings = $(element).is("select")
              ? $(element)
              : $(element).find("select").addBack().last();
            $(element)
              .dropdown({
                clearable: $settings.data("clearable") || false,
                direction: $settings.data("direction") || "auto",
                allowAdditions: $settings.data("additions") || false,
                hideAdditions: false,
                placeholder: $settings.data("placeholder") || "auto",
                on: $settings.data("on") || "click",
                values: $settings.data("values"),
                fullTextSearch: true,
                forceSelection: $settings.data("forceSelection") || false,
                selectOnKeydown: false,
              })
              .closest(".dropdown")
              .data("dropdown-loaded", true);
          }
        });
    },
    healNativeDropdowns() {
      // On the full-page event view the form mounts (and initDropdowns runs)
      // before show.vue commits the event to the store; the re-render then
      // patches over Fomantic's generated DOM, leaving select-backed
      // dropdowns scrambled: blank .text, empty .menu nested inside the
      // caret icon — Status shows nothing and can't be changed. Rebuild any
      // initialized dropdown whose select has options but whose menu is
      // empty. Native-only: the web experience keeps its exact markup
      // (pixel-parity mandate) — desktop edits events through the calendar
      // modal, which mounts after the store is populated and never scrambles.
      if (!this.isNativeApp) return;
      const $this = this;
      $(this.$el)
        .find(".ui.dropdown")
        .each((i, element) => {
          const $el = $(element);
          if (!$el.data("dropdown-loaded")) return;
          const select = $el.find("select").get(0);
          if (!select || select.options.length === 0) return;
          if (!$el.find(".menu .item").length) {
            $el.dropdown("destroy");
            $el.children(":not(select)").remove();
            $el.removeData("dropdown-loaded");
            $this.initDropdowns();
          }
          // Vue patches select.value directly (no change event), so Fomantic's
          // .text never learns about a value set after init — sync it. (Can't
          // compare against dropdown("get value"): select-backed dropdowns
          // read that straight from the select, so it always "matches".)
          const shownText = $el.children(".text").text().trim();
          if (select.value && !shownText) {
            $el.dropdown("set selected", select.value);
          }
        });
    },
    initForm() {
      const $this = this;

      this.showEndDate = $this.event.ends_at != null;

      if (this.canUpdateCalendar) {
        this.loadTags();
        this.$store.dispatch("sponsor/loadAll");
      }
      this.initDropdowns();

      const thisEl = $(this.$el);
      thisEl.find("#event_name").focus();
      const startDateCalendar = thisEl.find("#start_date");
      startDateCalendar.calendar({
        selectAdjacentDays: true,
        endCalendar: $("#end_date"),
        disableMinute: true,
        onChange(date, text) {
          thisEl.find("#start_date input").trigger("change");
          $this.event.starts_at = text;
        },
        onHidden() {
          // When calendar closes, capture whatever is in the input
          const inputVal = startDateCalendar.find('input').val();
          if (inputVal && inputVal !== $this.event.starts_at) {
            $this.event.starts_at = inputVal;
            thisEl.find("#start_date input").trigger("change");
          }
        }
      });
      const endDateCalendar = thisEl.find("#end_date");
      endDateCalendar.calendar({
        selectAdjacentDays: true,
        startCalendar: $("#start_date"),
        disableMinute: true,
        onChange(date, text) {
          thisEl.find("#end_date input").trigger("change");
          $this.event.ends_at = text;
        },
        onHidden() {
          // When calendar closes, capture whatever is in the input
          const inputVal = endDateCalendar.find('input').val();
          if (inputVal && inputVal !== $this.event.ends_at) {
            $this.event.ends_at = inputVal;
            thisEl.find("#end_date input").trigger("change");
          }
        }
      });

      $(this.$el).find(".ui.checkbox").checkbox();
    },
    closeModal() {
      window.EventBus.$emit("closeEventModal");
    },
    loadEventModal() {
      window.EventBus.$emit("openEventModal", this.event.url);
    },
    debouncedUpdate: _.debounce(
      function () {
        // Linked start/end date pickers fire onChange on both calendars
        // during initForm()'s mount-time setup (to enforce the start<=end
        // constraint), which reassigns event.ends_at before the user has
        // typed a name. Without this guard that fires a doomed create
        // (Event requires a name) for every new event opened, surfacing as
        // a spurious 400 on POST /calendars/:id/events.json.
        if (!this.loading && (this.event.id || this.event.name)) {
          this.save();
        }
      },
      750,
      { maxWait: 5000 }
    ),
  },
  watch: {
    // "event.description": _.throttle(function(val, old) {
    //   if (!old) return;
    //   this.debouncedUpdate();
    // }, 1500),
    "event.description": function (val, old) {
      if (!old) return;
      this.debouncedUpdate();
    },
    "event.name": function (val, old) {
      if (!old) return;
      this.debouncedUpdate();
    },
    "event.starts_at": function (val, old) {
      if (!old) return;
      this.debouncedUpdate();
    },
    "event.ends_at": function (val, old) {
      if (val != old) {
        this.debouncedUpdate();
      }
    },
    "event.rate": function (val, old) {
      if (!old) return;
      this.debouncedUpdate();
    },
    "event.aasm_state": function (val, old) {
      if (!old) return;
      this.debouncedUpdate();
    },
    "event.calendar_id": function (val, old) {
      if (!old) return;
      this.debouncedUpdate();
    },
    "event.event_category_id": function (val, old) {
      // if (!old) return;
      if (val != old) {
        this.debouncedUpdate();
        this.reloadSuggestedTasks();
      }
    },
    "event.user_id": function (val, old) {
      if (!old) return;
      this.debouncedUpdate();
    },
    "event.tag_list": function (val, old) {
      if (!old) return;
      this.debouncedUpdate();
    },
    "event.organization_id": function (val, old) {
      if (val != old) {
        this.debouncedUpdate();
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initDropdowns();
      this.initForm();
    });
  },
  updated() {
    this.$nextTick(() => {
      this.initDropdowns();
      this.healNativeDropdowns();
    });
  },
};
</script>

<style lang="scss" scoped>
@import "~vue-resize/dist/vue-resize.css";

.editor ::v-deep {
  div[style*="sticky"] {
    top: -1em !important;
  }
}

.dropdown.tags {
  padding-left: 2.67142857em !important;
  padding-right: 1em !important;
}

.multiple.selection.dropdown {
  > i.icon.left {
    cursor: default;
    pointer-events: none;
    position: absolute;
    width: auto;
    height: auto;
    line-height: 1.21428571em;
    top: 0.78571429em;
    left: 1em;
    z-index: 3;
    margin: -0.78571429em;
    padding: 0.91666667em;
    opacity: 0.5;
    transition: opacity 0.1s ease;
  }
}

// Phones / narrow mobile web: mobile.css.scss pins this Delete/Save footer
// (sticky) behind a floating fade gradient designed for the scrolling
// modal; at rest — and always on the full-page event view — the fade
// renders as a stray gray band above the buttons. Keep the sticky footer,
// drop the artifact: opaque surface with a hairline separator instead.
// Selector mirrors mobile.css.scss's so these rules win on specificity.
@media only screen and (max-width: 768px) {
  .modal .event div[data-tab="edit"] .form .clearing:last-child {
    &:before {
      display: none;
    }

    &:after {
      top: 0;
      border-top: 1px solid rgba(34, 36, 38, 0.1);
    }
  }
}
</style>
